import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { formatCell, formatDateTime, isNumericKind } from './formatCell';

/**
 * A report's answer on screen — the one place it is drawn.
 *
 * The viewer, the builder's live preview and an Ask AI answer all hand the same
 * server payload to this component, so a figure can never be shown two
 * different ways depending on which screen a person happened to be on. Loading,
 * refusal, "nothing yet" and "no rows" are states of the same thing and live
 * here too.
 *
 * `previewRows` caps what is drawn (the builder shows the first 20 while a
 * person is still choosing columns); the footer then says so plainly instead of
 * pretending the report is that short.
 */
const ResultTable = ({
    result,
    running = false,
    error = '',
    caption = '',
    previewRows = null,
    runningText = 'Running the report…',
    idleText = '',
    emptyText = 'No rows for this period. Widen the period or clear a filter.',
}) => {
    if (running) {
        return <div className="rc-panel-message">{runningText}</div>;
    }
    if (error) {
        return (
            <div className="rc-error">
                <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
            </div>
        );
    }
    if (!result) {
        return idleText ? <div className="rc-panel-message">{idleText}</div> : null;
    }

    const columns = result.columns || [];
    const allRows = result.rows || [];
    const rows = previewRows ? allRows.slice(0, previewRows) : allRows;

    const notes = (result.notes || []).map((note) => (
        <div key={note} className="rc-note">
            <FontAwesomeIcon icon={faCircleInfo} /> {note}
        </div>
    ));

    // An empty result still says what it knows (a voided document whose reversing
    // note is pending, a document left out): the notes are not tied to the rows.
    if (allRows.length === 0) {
        return (
            <>
                <div className="rc-panel-message">{emptyText}</div>
                {notes.length > 0 && <div className="rc-table-footer">{notes}</div>}
            </>
        );
    }

    return (
        <>
            <div className="rc-table-scroll">
                <table className="rc-table">
                    <caption className="rc-sr-only">{caption}</caption>
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    scope="col"
                                    className={isNumericKind(column.kind) ? 'rc-num' : ''}
                                >
                                    {column.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            // Rows carry no id of their own — the server sends values
                            // only — so position is the only key there is.
                            <tr key={rowIndex}>
                                {columns.map((column, cellIndex) => (
                                    <td
                                        key={column.key}
                                        className={isNumericKind(column.kind) ? 'rc-num' : ''}
                                    >
                                        {formatCell(row[cellIndex], column.kind)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {result.totals && (
                            <tr className="rc-totals-row">
                                {columns.map((column, cellIndex) => (
                                    <td
                                        key={column.key}
                                        className={isNumericKind(column.kind) ? 'rc-num' : ''}
                                    >
                                        {formatCell(result.totals[cellIndex], column.kind)}
                                    </td>
                                ))}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="rc-table-footer">
                {previewRows ? (
                    <div className="rc-table-count">
                        Preview of the first {formatCell(rows.length, 'integer')} of{' '}
                        {formatCell(result.total_rows, 'integer')} rows.
                    </div>
                ) : (
                    <>
                        <div className="rc-table-count">
                            {formatCell(result.total_rows, 'integer')} rows
                            {' · figures as at '}
                            {formatDateTime(result.as_at)}
                        </div>
                        {result.shown_rows < result.total_rows && (
                            <div className="rc-table-count">
                                Showing the first {formatCell(result.shown_rows, 'integer')} of{' '}
                                {formatCell(result.total_rows, 'integer')}. The download has all of them.
                            </div>
                        )}
                    </>
                )}
                {notes}
            </div>
        </>
    );
};

export default ResultTable;
